import {
  ArrowRight,
  BarChart3,
  ExternalLink,
  FileText,
  FolderKanban,
  Globe2,
  Layers3,
  MessageSquareText,
  Palette,
  Settings2,
  ShieldCheck,
  Sparkles,
  Workflow,
  Target,
  UserRound,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { TRUEVANCE_BRAND_NAME, TruevanceBrandHeader, useTruevanceBranding } from "./TruevanceBranding";
import { calculateTruevanceMetrics, formatCurrency, formatPercent, initializeTruevanceStore, type TruevanceStore } from "./TruevanceConnectData";
import { loadTruevanceWorkspace } from "./TruevanceConnectApi";

type BusinessCenterKey =
  | "home"
  | "sales-center"
  | "assessment-center"
  | "proposal-center"
  | "client-center"
  | "project-center"
  | "communication-center"
  | "analytics-center"
  | "settings-center";

type CenterTone = "light" | "dark";

interface CenterKpi {
  label: string;
  value: string | number;
  note: string;
  tone?: CenterTone;
}

interface CenterLink {
  label: string;
  to: string;
  description: string;
  state?: "Ready" | "Planned" | "Legacy";
}

interface CenterSection {
  title: string;
  description: string;
  items: Array<{
    title: string;
    description: string;
    state: "Live" | "Ready" | "Planned";
    to?: string;
  }>;
}

interface CenterDefinition {
  eyebrow: string;
  title: string;
  description: string;
  heroNote: string;
  lifecycleLabel: string;
  kpis: (store: TruevanceStore | null) => CenterKpi[];
  links: CenterLink[];
  sections: CenterSection[];
  emptyStateTitle: string;
  emptyStateDescription: string;
}

const businessLifecycle = [
  "Website Inquiry",
  "Lead Intake",
  "Discovery Call",
  "Assessment",
  "Proposal",
  "Agreement",
  "Invoice",
  "Client Onboarding",
  "Project Delivery",
  "Follow-Up",
];

const coreModuleRoadmap = [
  {
    label: "Dashboard",
    state: "Ready" as const,
    description: "Business Center overview, executive visibility, and KPI entry point.",
    to: "/admin/executive-dashboard",
  },
  {
    label: "CRM / Leads",
    state: "Ready" as const,
    description: "Lead intake, discovery flow, and opportunity tracking.",
    to: "/admin/leads",
  },
  {
    label: "Client Records",
    state: "Ready" as const,
    description: "Brand-scoped client profiles, contacts, and history.",
    to: "/admin/truevance-client-center",
  },
  {
    label: "Projects",
    state: "Ready" as const,
    description: "Project delivery records, milestones, and progress.",
    to: "/admin/truevance-project-center",
  },
  {
    label: "Tasks Center",
    state: "Ready" as const,
    description: "Dedicated task management for client and project work.",
    to: "/admin/truevance-tasks",
  },
  {
    label: "Document Center",
    state: "Ready" as const,
    description: "Client folders, project folders, document records, and upload placeholders.",
    to: "/admin/truevance-document-center",
  },
  {
    label: "Universal User Management",
    state: "Ready" as const,
    description: "Parent-company accounts, scoped users, and access assignment foundation.",
    to: "/admin/universal-user-management",
  },
      {
        label: "Client Onboarding Center",
        state: "Ready" as const,
        description: "Shared onboarding flow for company creation, users, branding, and workspace assignment.",
        to: "/admin/client-onboarding-center",
      },
      {
        label: "Workspace Provisioning Center",
        state: "Ready" as const,
        description: "Create, activate, brand, and govern shared workspace shells.",
        to: "/admin/workspace-provisioning-center",
      },
      {
        label: "White Label Branding Center",
        state: "Ready" as const,
        description: "Shared branding profiles, portal styling, documents, emails, and domains.",
        to: "/admin/white-label-branding-center",
      },
      {
        label: "Automation Engine",
        state: "Ready" as const,
        description: "Reusable trigger, condition, and action workflows across all tenants.",
        to: "/admin/automation-engine",
      },
      {
        label: "Roles & Permissions",
        state: "Ready" as const,
        description: "Role definitions, assignment controls, and permission mapping.",
        to: "/admin/truevance-roles-center",
  },
  {
    label: "Reporting",
    state: "Ready" as const,
    description: "Executive reporting, analytics, and operational visibility.",
    to: "/admin/truevance-analytics-center",
  },
  {
    label: "Billing placeholder",
    state: "Ready" as const,
    description: "Payment settings and payment records exist as the billing bridge.",
    to: "/admin/payment-records",
  },
  {
    label: "Universal Client Portal",
    state: "Ready" as const,
    description: "Shared client portal foundation for proposals, agreements, payments, and projects.",
    to: "/client/portal",
  },
  {
    label: "CollabRecover AR Solutions",
    state: "Ready" as const,
    description: "Recovery dashboard, customer accounts, ticklers, promises, and white-label tenant scaffolding.",
    to: "/admin/ar-recovery",
  },
];

const centerNavigation = [
  {
    label: "Sales & Lead Center",
    to: "/admin/truevance-sales-center",
    description: "Lead intake, discovery calls, opportunities, pipeline stages, assignments, and follow-ups.",
    icon: Target,
  },
  {
    label: "Assessment Center",
    to: "/admin/truevance-assessment-center",
    description: "Business assessments, digital reviews, workflow audits, findings, and recommendations.",
    icon: ShieldCheck,
  },
  {
    label: "Proposal Center",
    to: "/admin/truevance-proposal-center",
    description: "Proposal records, service recommendations, scope building, and approval tracking.",
    icon: FileText,
  },
  {
    label: "Client Center",
    to: "/admin/truevance-client-center",
    description: "Client profiles, contacts, organizations, agreements, and documents.",
    icon: UserRound,
  },
  {
    label: "Project Center",
    to: "/admin/truevance-project-center",
    description: "Active consulting projects, milestones, deliverables, tasks, and progress.",
    icon: Layers3,
  },
  {
    label: "Tasks Center",
    to: "/admin/truevance-tasks",
    description: "Task tracking across clients, projects, and internal follow-up work.",
    icon: FolderKanban,
  },
  {
    label: "Document Center",
    to: "/admin/truevance-document-center",
    description: "Client and project folders, document records, and placeholder uploads.",
    icon: FileText,
  },
  {
    label: "Universal User Management",
    to: "/admin/universal-user-management",
    description: "Parent-company users, scoped roles, and access assignment foundations.",
    icon: UserRound,
  },
  {
    label: "Client Onboarding Center",
    to: "/admin/client-onboarding-center",
    description: "Shared onboarding flow for company creation, users, branding, and workspace assignment.",
    icon: UserRound,
  },
  {
    label: "Workspace Provisioning Center",
    to: "/admin/workspace-provisioning-center",
    description: "Create, activate, brand, and govern shared workspace shells.",
    icon: Globe2,
  },
  {
    label: "White Label Branding Center",
    to: "/admin/white-label-branding-center",
    description: "Shared branding profiles, portal styling, documents, emails, and domains.",
    icon: Palette,
  },
  {
    label: "Automation Engine",
    to: "/admin/automation-engine",
    description: "Reusable trigger, condition, and action workflows across all tenants.",
    icon: Workflow,
  },
  {
    label: "Roles & Permissions",
    to: "/admin/truevance-roles-center",
    description: "Role definitions, permission mappings, and assignment controls.",
    icon: ShieldCheck,
  },
  {
    label: "Communication Center",
    to: "/admin/truevance-communication-center",
    description: "Notes, activity timelines, email history, and follow-up tracking.",
    icon: MessageSquareText,
  },
  {
    label: "Analytics Center",
    to: "/admin/truevance-analytics-center",
    description: "Lead, opportunity, conversion, client, and project metrics.",
    icon: BarChart3,
  },
  {
    label: "Settings Center",
    to: "/admin/truevance-settings-center",
    description: "Brand, workflow, routing, notification, and access settings.",
    icon: Settings2,
  },
  {
    label: "CollabRecover AR Solutions",
    to: "/admin/ar-recovery",
    description: "Recovery dashboard, promise tracking, ticklers, and tenant-scoped reporting.",
    icon: BarChart3,
  },
] as const;

const definitions: Record<BusinessCenterKey, CenterDefinition> = {
  home: {
    eyebrow: TRUEVANCE_BRAND_NAME,
    title: TRUEVANCE_BRAND_NAME,
    description:
      "A private consulting operations command center for managing engagements from inquiry through implementation and follow-up.",
    heroNote: "Route-scoped business operations workspace",
    lifecycleLabel: "Future consulting lifecycle",
    kpis: (store) => {
      const metrics = store ? calculateTruevanceMetrics(store) : null;
      return [
        { label: "Open Opportunities", value: metrics?.openOpportunities ?? 0, note: "Active sales and discovery work.", tone: "dark" },
        { label: "Active Assessments", value: metrics?.activeAssessments ?? 0, note: "Live diagnosis and recommendation work.", tone: "light" },
        { label: "Active Projects", value: metrics?.activeProjects ?? 0, note: "Ongoing implementation work.", tone: "light" },
        { label: "Consulting Revenue", value: formatCurrency(metrics?.consultingRevenue ?? 0), note: "Revenue recorded across projects.", tone: "dark" },
      ];
    },
    links: [
      {
        label: "Sales & Lead Center",
        to: "/admin/truevance-sales-center",
        description: "Lead intake, discovery, assignments, opportunities, and follow-up tracking.",
        state: "Ready",
      },
      {
        label: "Assessment Center",
        to: "/admin/truevance-assessment-center",
        description: "Assessments, findings, workflow audits, and recommendations.",
        state: "Ready",
      },
      {
        label: "Proposal Center",
        to: "/admin/truevance-proposal-center",
        description: "Proposal records, scope building, and approvals.",
        state: "Ready",
      },
      {
        label: "Client Center",
        to: "/admin/truevance-client-center",
        description: "Client records, contacts, agreements, histories, and portal preparation.",
        state: "Ready",
      },
      {
        label: "Project Center",
        to: "/admin/truevance-project-center",
        description: "Active projects, milestones, deliverables, and tasks.",
        state: "Ready",
      },
      {
        label: "Document Center",
        to: "/admin/truevance-document-center",
        description: "Client and project folders, document records, and upload placeholders.",
        state: "Ready",
      },
      {
        label: "Universal User Management",
        to: "/admin/universal-user-management",
        description: "Parent-company users, scoped roles, and access assignment foundations.",
        state: "Ready",
      },
      {
        label: "Client Onboarding Center",
        to: "/admin/client-onboarding-center",
        description: "Shared onboarding flow for company creation, users, branding, and workspace assignment.",
        state: "Ready",
      },
      {
        label: "Workspace Provisioning Center",
        to: "/admin/workspace-provisioning-center",
        description: "Create, activate, brand, and govern shared workspace shells.",
        state: "Ready",
      },
      {
        label: "White Label Branding Center",
        to: "/admin/white-label-branding-center",
        description: "Shared branding profiles, portal styling, documents, emails, and domains.",
        state: "Ready",
      },
      {
        label: "Automation Engine",
        to: "/admin/automation-engine",
        description: "Reusable trigger, condition, and action workflows across all tenants.",
        state: "Ready",
      },
      {
        label: "Roles & Permissions",
        to: "/admin/truevance-roles-center",
        description: "Role definitions, user assignment, and permission matrix foundations.",
        state: "Ready",
      },
      {
        label: "Communication Center",
        to: "/admin/truevance-communication-center",
        description: "Notes, activity timeline, email history, and follow-ups.",
        state: "Ready",
      },
      {
        label: "Analytics Center",
        to: "/admin/truevance-analytics-center",
        description: "Executive consulting metrics and performance visibility.",
        state: "Ready",
      },
      {
        label: "Settings Center",
        to: "/admin/truevance-settings-center",
        description: "Brand, routing, notifications, and access controls.",
        state: "Planned",
      },
    ],
    sections: [
      {
        title: "Operating Principles",
        description: "The Business Center is designed to manage consulting work, not to market services.",
        items: [
          { title: "Private workspace", description: "Admin-only visibility, route-scoped branding, and operational access.", state: "Live" },
          { title: "Lifecycle-first design", description: "Every screen is mapped to inquiry, discovery, assessment, proposal, agreement, invoice, onboarding, delivery, and follow-up.", state: "Live" },
          { title: "Reusable foundation", description: "Layouts, cards, navigation, and status surfaces are structured for future reuse.", state: "Live" },
          { title: "Future schema layer", description: "Proposal, client, communication, and settings records can expand later without changing the UI shell.", state: "Planned" },
        ],
      },
      {
        title: "Reusable core modules",
        description: "This is the current build map for the Business Center OS core, with working modules first and future modules kept visible.",
        items: coreModuleRoadmap.map((module) => ({
          title: module.label,
          description: module.description,
          state: module.state,
          to: module.to,
        })),
      },
    ],
    emptyStateTitle: "Business Center foundation is in place",
    emptyStateDescription:
      "The brand layer, route shell, and center navigation are live. Dedicated record models for proposal, client, communication, and settings workspaces can be added in the next schema phase.",
  },
  "sales-center": {
    eyebrow: TRUEVANCE_BRAND_NAME,
    title: "Sales & Lead Center",
    description:
      "Manage lead intake, discovery calls, opportunities, assignments, pipeline stages, and follow-ups from one consulting command center.",
    heroNote: "Sales and discovery workspace",
    lifecycleLabel: "Sales lifecycle focus",
    kpis: (store) => {
      const metrics = store ? calculateTruevanceMetrics(store) : null;
      return [
        { label: "Open Opportunities", value: metrics?.openOpportunities ?? 0, note: "Consulting opportunities in motion.", tone: "dark" },
        { label: "Pipeline Value", value: formatCurrency(metrics?.pipelineValue ?? 0), note: "Weighted by probability.", tone: "light" },
        { label: "Win Rate", value: formatPercent(metrics?.winRate ?? 0), note: "Closed opportunities won.", tone: "light" },
        { label: "Follow-Ups", value: Math.max(1, store?.opportunities.length || 0), note: "Leads and opportunities needing attention.", tone: "dark" },
      ];
    },
    links: [
      { label: "Opportunity Management", to: "/admin/truevance-opportunities", description: "Persistent opportunity records and next actions.", state: "Ready" },
      { label: "Consulting Pipeline", to: "/admin/consulting-pipeline", description: "Stage-based consulting journey visibility.", state: "Legacy" },
      { label: "Lead Management", to: "/admin/leads", description: "Lead capture, status history, tagging, and intake tracking.", state: "Ready" },
      { label: "Executive Dashboard", to: "/admin/executive-dashboard#follow-up-center", description: "Follow-up center and conversion visibility.", state: "Ready" },
    ],
    sections: [
      {
        title: "Sales operating zones",
        description: "These panels reflect the work that happens between inquiry and proposal readiness.",
        items: [
          { title: "Lead intake", description: "Capture source, assignment, contact details, and intent in a single handoff.", state: "Live", to: "/admin/leads" },
          { title: "Discovery calls", description: "Route discovery conversations into the next stage of the journey.", state: "Planned", to: "/admin/scheduler-settings" },
          { title: "Opportunities", description: "Track pipeline stage, probability, and next action.", state: "Live", to: "/admin/truevance-opportunities" },
          { title: "Follow-ups", description: "Keep next actions visible for every active prospect.", state: "Live", to: "/admin/executive-dashboard#follow-up-center" },
        ],
      },
      {
        title: "Pipeline stages",
        description: "The sales center is shaped around the future consultation lifecycle.",
        items: businessLifecycle.slice(0, 5).map((label) => ({
          title: label,
          description: "Operational stage in the consulting journey.",
          state: "Live" as const,
        })),
      },
    ],
    emptyStateTitle: "No sales records in this view",
    emptyStateDescription: "If no live opportunities are present, this view still shows the sales operating model and the next steps that will be tracked here.",
  },
  "assessment-center": {
    eyebrow: TRUEVANCE_BRAND_NAME,
    title: "Assessment Center",
    description:
      "Review business assessments, digital presence audits, workflow reviews, customer experience observations, findings, and recommendations.",
    heroNote: "Assessment and recommendation workspace",
    lifecycleLabel: "Assessment lifecycle focus",
    kpis: (store) => {
      const metrics = store ? calculateTruevanceMetrics(store) : null;
      return [
        { label: "Active Assessments", value: metrics?.activeAssessments ?? 0, note: "Assessments in progress.", tone: "light" },
        { label: "Active Recommendations", value: metrics?.activeRecommendations ?? 0, note: "Recommendations being delivered or refined.", tone: "dark" },
        { label: "Implementation Progress", value: formatPercent(metrics?.implementationProgress ?? 0), note: "Average roadmap completion.", tone: "light" },
        { label: "Assessment Records", value: store?.assessments.length ?? 0, note: "Persistent assessment records available today.", tone: "dark" },
      ];
    },
    links: [
      { label: "Assessment Records", to: "/admin/truevance-assessments", description: "Persistent business assessment records.", state: "Ready" },
      { label: "Consulting Assessments", to: "/admin/consulting-assessments", description: "Structured assessment engine with scores and recommendations.", state: "Ready" },
      { label: "Consulting Recommendations", to: "/admin/truevance-recommendations", description: "Solution recommendations linked to assessments.", state: "Ready" },
      { label: "Assessment Framework", to: "/admin/assessment-framework", description: "Framework for challenges, opportunities, and suggested solutions.", state: "Ready" },
    ],
    sections: [
      {
        title: "Assessment workstreams",
        description: "Use this center to turn discovery notes into structured recommendations.",
        items: [
          { title: "Business assessments", description: "Operational, strategic, and organizational review points.", state: "Live", to: "/admin/truevance-assessments" },
          { title: "Digital presence reviews", description: "Review website and portal experience before proposing improvements.", state: "Planned" },
          { title: "Workflow audits", description: "Document bottlenecks, handoffs, and automation opportunities.", state: "Live", to: "/admin/consulting-assessments" },
          { title: "Recommendations", description: "Translate findings into clear next steps and service alignment.", state: "Live", to: "/admin/truevance-recommendations" },
        ],
      },
      {
        title: "Assessment lifecycle",
        description: "Every assessment should lead to a recommendation, a proposal, or a roadmap.",
        items: businessLifecycle.slice(2, 7).map((label) => ({
          title: label,
          description: "Stage aligned to assessment-to-solution delivery.",
          state: "Live" as const,
        })),
      },
    ],
    emptyStateTitle: "Assessment records will appear here when connected",
    emptyStateDescription:
      "This center already defines the operating surface for audits and assessments. The data model can grow later without changing the route structure.",
  },
  "proposal-center": {
    eyebrow: TRUEVANCE_BRAND_NAME,
    title: "Proposal Center",
    description:
      "Track proposal records, scope planning, service recommendations, approval status, and the next step toward agreement.",
    heroNote: "Proposal and scope workspace",
    lifecycleLabel: "Proposal lifecycle focus",
    kpis: (store) => [
      { label: "Proposal Records", value: 0, note: "Dedicated consulting proposal records arrive in the next schema phase.", tone: "dark" },
      { label: "Scope Builder", value: "Ready", note: "Structured scope and service recommendations are planned.", tone: "light" },
      { label: "Approval Tracking", value: "Ready", note: "Proposal approval states will follow.", tone: "light" },
      { label: "Related Workspaces", value: store?.opportunities.length ?? 0, note: "Current opportunities that can feed future proposals.", tone: "dark" },
    ],
    links: [
      { label: "Create Proposal", to: "/admin/proposals", description: "Existing Business Center OS proposal workspace.", state: "Legacy" },
      { label: "Proposal Dashboard", to: "/admin/proposal-dashboard", description: "Executive proposal overview and workflow status.", state: "Ready" },
      { label: "Consulting Recommendations", to: "/admin/truevance-recommendations", description: "Recommended solutions that may feed scope creation.", state: "Ready" },
      { label: "Opportunity Management", to: "/admin/truevance-opportunities", description: "Qualified opportunities ready for proposal building.", state: "Ready" },
    ],
    sections: [
      {
        title: "What the proposal center will track",
        description: "This route is ready for a dedicated consulting proposal data layer when approved.",
        items: [
          { title: "Proposal records", description: "Core records for client-ready consulting proposals.", state: "Planned" },
          { title: "Service recommendations", description: "Recommended services aligned to assessment outcomes.", state: "Planned" },
          { title: "Scope builder", description: "Package services, add-ons, and pricing into a reusable scope workflow.", state: "Planned" },
          { title: "Approval tracking", description: "Signal readiness to send, approve, or revise a proposal.", state: "Planned" },
        ],
      },
      {
        title: "Bridge to current forms",
        description: "Until a dedicated consulting proposal model is approved, the existing Business Center OS proposal workspace remains the operational bridge.",
        items: [
          { title: "Existing proposal workflow", description: "The current admin proposal workspace remains functional for the existing portal release.", state: "Live", to: "/admin/proposals" },
          { title: "Proposal dashboard", description: "Use the current dashboard for immediate workflow visibility.", state: "Live", to: "/admin/proposal-dashboard" },
        ],
      },
    ],
    emptyStateTitle: "Proposal records are a planned expansion",
    emptyStateDescription:
      "This page intentionally shows the route shell and the future operating model while the separate consulting proposal schema is approved.",
  },
  "client-center": {
    eyebrow: TRUEVANCE_BRAND_NAME,
    title: "Client Center",
    description:
      "Manage client profiles, contacts, organizations, agreements, and documents inside a clean consulting workspace.",
    heroNote: "Client operations workspace",
    lifecycleLabel: "Client lifecycle focus",
    kpis: (store) => {
      const metrics = store ? calculateTruevanceMetrics(store) : null;
      return [
        { label: "Active Clients", value: metrics?.completedProjects ?? 0, note: "Clients currently in delivery or completion.", tone: "dark" },
        { label: "Agreements", value: "Ready", note: "Agreement records remain in the shared Business Center OS layer.", tone: "light" },
        { label: "Documents", value: "Ready", note: "Document handling can be connected later.", tone: "light" },
        { label: "Client Records", value: store?.projects.length ?? 0, note: "Current relationships already visible in the system.", tone: "dark" },
      ];
    },
    links: [
      { label: "Client Center", to: "/admin/truevance-client-center", description: "Brand-scoped client records, contact records, notes, and history.", state: "Ready" },
      { label: "Client Engagement Dashboard", to: "/admin/client-engagement-dashboard", description: "Active clients, assessments, implementations, and opportunities.", state: "Ready" },
      { label: "Agreement Records", to: "/admin/agreement-records", description: "Agreement status, signature details, and linked records.", state: "Ready" },
      { label: "Truevance Business Center", to: "/admin/truevance-business-center", description: "Return to the Truevance workspace overview.", state: "Ready" },
    ],
    sections: [
      {
        title: "Client record surfaces",
        description: "The client center already uses these slots and can grow without changing the route structure.",
        items: [
          { title: "Client profiles", description: "Primary profile view for each client organization.", state: "Live" },
          { title: "Contacts", description: "Stakeholders, decision makers, and working contacts.", state: "Live" },
          { title: "Organizations", description: "Company-level account details and relationship hierarchy.", state: "Live" },
          { title: "Documents", description: "Proposal, agreement, and delivery artifacts.", state: "Live" },
        ],
      },
      {
        title: "Current bridge points",
        description: "Existing workspaces already hold the live client-facing operational records.",
        items: [
          { title: "Client directory", description: "The current client directory stays operational.", state: "Live", to: "/admin/forms-client-directory" },
          { title: "Agreement workspace", description: "Agreement records are already operational.", state: "Live", to: "/admin/agreement-records" },
        ],
      },
    ],
    emptyStateTitle: "Client records are live in the Business Center",
    emptyStateDescription:
      "The route, shell, and navigation are ready now so the live client model can evolve without rewriting the workspace layout.",
  },
  "project-center": {
    eyebrow: TRUEVANCE_BRAND_NAME,
    title: "Project Center",
    description:
      "Track active consulting projects, deliverables, milestones, tasks, and progress from a calm executive workspace.",
    heroNote: "Project delivery workspace",
    lifecycleLabel: "Project lifecycle focus",
    kpis: (store) => {
      const metrics = store ? calculateTruevanceMetrics(store) : null;
      return [
        { label: "Active Projects", value: metrics?.activeProjects ?? 0, note: "Delivery work currently underway.", tone: "dark" },
        { label: "Completed Projects", value: metrics?.completedProjects ?? 0, note: "Work already closed out.", tone: "light" },
        { label: "Project Completion", value: formatPercent(metrics?.projectCompletionRate ?? 0), note: "Average completion across projects.", tone: "light" },
        { label: "Average Project Value", value: formatCurrency(metrics?.averageProjectValue ?? 0), note: "Value per active or completed project.", tone: "dark" },
      ];
    },
    links: [
      { label: "Consulting Projects", to: "/admin/truevance-projects", description: "Persistent project delivery records and progress tracking.", state: "Ready" },
      { label: "Implementation Roadmaps", to: "/admin/implementation-roadmaps", description: "Quick wins through long-term delivery plans.", state: "Ready" },
      { label: "Executive Dashboard", to: "/admin/executive-dashboard", description: "Project metrics and follow-up oversight.", state: "Ready" },
      { label: "Project Dashboard", to: "/admin/project-dashboard", description: "Existing internal Business Center OS project support.", state: "Legacy" },
    ],
    sections: [
      {
        title: "Project delivery zones",
        description: "This page is tuned to the structure of consulting implementation work.",
        items: [
          { title: "Active projects", description: "Visibility into ongoing implementation and optimization work.", state: "Live", to: "/admin/truevance-projects" },
          { title: "Deliverables", description: "Artifacts, milestones, and outputs that prove progress.", state: "Planned" },
          { title: "Milestones", description: "Important progress markers across the project lifecycle.", state: "Planned" },
          { title: "Tasks", description: "Operational follow-ups and work items for the team.", state: "Planned" },
        ],
      },
      {
        title: "Project lifecycle",
        description: "Discovery through optimization is the future operating pattern for delivery work.",
        items: businessLifecycle.slice(3, 9).map((label) => ({
          title: label,
          description: "Stage reflected in client delivery and project management.",
          state: "Live" as const,
        })),
      },
    ],
    emptyStateTitle: "Project delivery shell is ready",
    emptyStateDescription:
      "The navigation, header, and layout are ready for future project records, deliverables, milestones, and task tracking.",
  },
  "communication-center": {
    eyebrow: TRUEVANCE_BRAND_NAME,
    title: "Communication Center",
    description:
      "Keep notes, activity timelines, email history, and follow-up tracking in a private consulting operations view.",
    heroNote: "Communication and activity workspace",
    lifecycleLabel: "Communication lifecycle focus",
    kpis: (store) => {
      const metrics = store ? calculateTruevanceMetrics(store) : null;
      return [
        { label: "Follow-Ups", value: store?.opportunities.length ?? 0, note: "Open items needing a response.", tone: "dark" },
        { label: "Activity Visibility", value: "Ready", note: "A timeline surface can be connected later.", tone: "light" },
        { label: "Email History", value: "Ready", note: "Message tracking will be added after schema approval.", tone: "light" },
        { label: "Pipeline Value", value: formatCurrency(metrics?.pipelineValue ?? 0), note: "Communication supports sales progress.", tone: "dark" },
      ];
    },
    links: [
      { label: "Executive Dashboard", to: "/admin/executive-dashboard#follow-up-center", description: "Follow-up center and reminders.", state: "Ready" },
      { label: "Lead Management", to: "/admin/leads", description: "Lead details and status history.", state: "Ready" },
      { label: "Activity Log", to: "/admin/activity-log", description: "Existing admin activity timeline for the broader portal.", state: "Legacy" },
      { label: "Discovery Call Notes", to: "/admin/discovery-call-notes", description: "Discovery support page already available in the admin portal.", state: "Ready" },
    ],
    sections: [
      {
        title: "Communication surfaces",
        description: "These cards define the future communication workspace without changing current data behavior.",
        items: [
          { title: "Notes", description: "Central notes per client, project, or opportunity.", state: "Planned" },
          { title: "Activity timeline", description: "Chronological log of updates and next steps.", state: "Planned" },
          { title: "Email history", description: "Message tracking and delivery history.", state: "Planned" },
          { title: "Follow-up tracking", description: "Next action reminders for the team.", state: "Planned" },
        ],
      },
      {
        title: "Current communication bridges",
        description: "The existing portal already has a few useful admin touchpoints while the dedicated communication layer is planned.",
        items: [
          { title: "Executive follow-up center", description: "Current place to manage post-meeting and sales follow-up.", state: "Live", to: "/admin/executive-dashboard#follow-up-center" },
          { title: "Activity log", description: "Existing admin activity surface for portal operations.", state: "Live", to: "/admin/activity-log" },
        ],
      },
    ],
    emptyStateTitle: "Communication workspace is live",
    emptyStateDescription:
      "This route now provides the operational surface for notes, activity, follow-ups, and history without touching the rest of the portal.",
  },
  "analytics-center": {
    eyebrow: TRUEVANCE_BRAND_NAME,
    title: "Analytics Center",
    description:
      "Review leads, opportunities, conversion metrics, active clients, project metrics, and consulting performance in one view.",
    heroNote: "Analytics and reporting workspace",
    lifecycleLabel: "Analytics lifecycle focus",
    kpis: (store) => {
      const metrics = store ? calculateTruevanceMetrics(store) : null;
      return [
        { label: "Open Opportunities", value: metrics?.openOpportunities ?? 0, note: "Live sales count.", tone: "dark" },
        { label: "Active Assessments", value: metrics?.activeAssessments ?? 0, note: "Assessments in motion.", tone: "light" },
        { label: "Active Projects", value: metrics?.activeProjects ?? 0, note: "Delivery work underway.", tone: "light" },
        { label: "Consulting Revenue", value: formatCurrency(metrics?.consultingRevenue ?? 0), note: "Revenue across project workspaces.", tone: "dark" },
      ];
    },
    links: [
      { label: "Executive Dashboard", to: "/admin/executive-dashboard", description: "Existing metrics hub and follow-up center.", state: "Ready" },
      { label: "Client Engagement Dashboard", to: "/admin/client-engagement-dashboard", description: "Operational consulting metrics and reusable patterns.", state: "Ready" },
      { label: "Truevance Connect Business Center", to: "/admin/truevance-business-center", description: "Return to the business center overview.", state: "Ready" },
      { label: "Proposal Dashboard", to: "/admin/proposal-dashboard", description: "Business Center OS proposal reporting and workflow view.", state: "Ready" },
    ],
    sections: [
      {
        title: "Analytics surfaces",
        description: "This center gathers the operational metrics the consulting team needs to stay aligned.",
        items: [
          { title: "Lead analytics", description: "Lead sources, intake volume, and discovery performance.", state: "Planned" },
          { title: "Opportunity analytics", description: "Stage conversion and weighted pipeline value.", state: "Live", to: "/admin/truevance-opportunities" },
          { title: "Client analytics", description: "Active client count and delivery progress.", state: "Planned" },
          { title: "Project analytics", description: "Project completion and revenue visibility.", state: "Live", to: "/admin/truevance-projects" },
        ],
      },
      {
        title: "Operational reporting bridge",
        description: "The current dashboard stack already provides a strong executive reporting baseline while this center grows.",
        items: [
          { title: "Executive dashboard", description: "The current production reporting layer remains the system of record for metrics.", state: "Live", to: "/admin/executive-dashboard" },
          { title: "Client engagement dashboard", description: "A second reporting surface for work-in-progress visibility.", state: "Live", to: "/admin/client-engagement-dashboard" },
        ],
      },
    ],
    emptyStateTitle: "Analytics surfaces can expand without a redesign",
    emptyStateDescription:
      "The navigation and card language are ready now so future reporting and multi-tenant metrics can be added without changing the Business Center shell.",
  },
  "settings-center": {
    eyebrow: TRUEVANCE_BRAND_NAME,
    title: "Settings Center",
    description:
      "Manage brand, workflow, routing, notifications, and access surfaces for the consulting operating layer.",
    heroNote: "Settings and control workspace",
    lifecycleLabel: "Settings lifecycle focus",
    kpis: () => [
      { label: "Brand Layer", value: "Ready", note: "Route-scoped Truevance styling is already active.", tone: "dark" },
      { label: "Workflow Routing", value: "Ready", note: "Navigation and shells are connected.", tone: "light" },
      { label: "Notifications", value: "Planned", note: "Email and automation controls can be refined later.", tone: "light" },
      { label: "Access Controls", value: "Ready", note: "Admin-only routing remains protected.", tone: "dark" },
    ],
    links: [
      { label: "Payment Settings", to: "/admin/payment-settings", description: "Tax, deposit, invoice, and payment defaults.", state: "Ready" },
      { label: "Scheduler Settings", to: "/admin/scheduler-settings", description: "Scheduling links, kickoff calls, and readiness.", state: "Ready" },
      { label: "Welcome Email Settings", to: "/admin/welcome-email-settings", description: "Welcome message configuration.", state: "Ready" },
      { label: "Onboarding Settings", to: "/admin/onboarding-settings", description: "Next steps and onboarding workflow controls.", state: "Ready" },
    ],
    sections: [
      {
        title: "Settings surfaces",
        description: "These panels will remain route-scoped and admin-only as the platform grows.",
        items: [
          { title: "Brand controls", description: "Logo, favicon, and visual language for the Business Center.", state: "Live" },
          { title: "Workflow routing", description: "Links between sales, proposals, onboarding, and delivery.", state: "Live", to: "/admin/truevance-settings-center" },
          { title: "Notifications", description: "Email delivery and follow-up controls.", state: "Planned" },
          { title: "Access controls", description: "Admin-only visibility and future permissions planning.", state: "Live" },
        ],
      },
      {
        title: "Operational settings bridge",
        description: "The current portal already exposes the key settings pages the Business Center needs to reference.",
        items: [
          { title: "Payment settings", description: "Existing billing configuration page.", state: "Live", to: "/admin/payment-settings" },
          { title: "Scheduler settings", description: "Existing scheduler workflow page.", state: "Live", to: "/admin/scheduler-settings" },
          { title: "Welcome and onboarding", description: "Existing transition and handoff workflows.", state: "Live", to: "/admin/welcome-email-settings" },
        ],
      },
    ],
    emptyStateTitle: "Settings controls are intentionally route-scoped",
    emptyStateDescription:
      "The settings area is ready to evolve without changing the rest of the platform, keeping other workspace-specific surfaces and Memory Media Vault untouched.",
  },
};

export function TruevanceBusinessCenterPage({ center }: { center: BusinessCenterKey }) {
  const details = definitions[center];
  const location = useLocation();
  const [workspaceData, setWorkspaceData] = useState<Awaited<ReturnType<typeof loadTruevanceWorkspace>> | null>(null);

  useTruevanceBranding(details.title, details.description);

  useEffect(() => {
    if (!location.hash) return;
    const target = document.getElementById(location.hash.slice(1));
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash]);

  useEffect(() => {
    let cancelled = false;
    loadTruevanceWorkspace()
      .then((data) => {
        if (!cancelled) setWorkspaceData(data);
      })
      .catch(() => {
        if (!cancelled) {
          setWorkspaceData({
            store: import.meta.env.DEV
              ? initializeTruevanceStore()
              : { opportunities: [], assessments: [], recommendations: [], roadmaps: [], projects: [] },
            serviceLibrary: [],
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!workspaceData) {
    return (
      <main className="truevance-shell truevance-business-shell min-h-screen text-[#F5F7FA]">
        <TruevanceBrandHeader />
        <section className="px-4 py-16 sm:px-6 lg:px-8">
          <div className="truevance-business-loading mx-auto grid max-w-4xl gap-4 rounded-3xl p-8 sm:p-10">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#17D4C4]">{TRUEVANCE_BRAND_NAME}</p>
            <h1 className="font-display text-4xl font-bold text-[#071A2F]">{details.title}</h1>
            <p className="text-sm font-semibold leading-8 text-[#334155]">Loading the Truevance Connect Business Center workspace and route-scoped layout.</p>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={`loading-${index}`} className="rounded-2xl border border-[rgba(34,224,208,.1)] bg-white/80 p-4">
                  <div className="h-3 w-24 rounded-full bg-[rgba(34,224,208,.18)]" />
                  <div className="mt-4 h-8 w-20 rounded-full bg-[rgba(7,26,47,.12)]" />
                  <div className="mt-4 h-3 w-full rounded-full bg-[rgba(7,26,47,.08)]" />
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    );
  }

  const store = workspaceData?.store || null;
  const kpis = details.kpis(store);

  return (
    <main className="truevance-shell truevance-business-shell min-h-screen text-[#F5F7FA]">
      <TruevanceBrandHeader />

      <section className="truevance-business-hero border-b border-[rgba(34,224,208,.16)] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[1.25fr_.75fr] lg:items-end">
          <div className="grid gap-4">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[rgba(34,224,208,.32)] bg-[rgba(7,26,47,.55)] px-3 py-2 text-xs font-black uppercase tracking-[0.22em] text-[#22E0D0]">
              <Sparkles className="size-4" aria-hidden="true" />
              {details.eyebrow}
            </div>
            <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">{details.title}</h1>
            <p className="max-w-3xl text-base font-semibold leading-8 text-[#E8EEF4]">{details.description}</p>
          </div>

          <article className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#22E0D0]">{details.heroNote}</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-[#F5F7FA]">Built for operational clarity</h2>
            <p className="mt-3 text-sm font-semibold leading-7 text-[#E8EEF4]">
              The public website attracts clients. This Business Center manages consulting work, follow-up, and delivery.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link to="/admin/truevance-business-center" className="admin-primary-button focus-ring">
                Truevance Business Center
              </Link>
              <Link to="/admin/executive-dashboard" className="admin-secondary-button focus-ring">
                Executive Dashboard
              </Link>
            </div>
          </article>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8">
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {kpis.map((metric) => (
              <article
                key={metric.label}
                className={`truevance-business-card truevance-business-card--${metric.tone === "dark" ? "dark" : "light"} rounded-2xl p-5`}
              >
                <p className={`text-xs font-black uppercase tracking-[0.18em] ${metric.tone === "dark" ? "text-[#22E0D0]" : "text-[#17D4C4]"}`}>
                  {metric.label}
                </p>
                <p className={`mt-2 font-display text-3xl font-bold ${metric.tone === "dark" ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>
                  {metric.value}
                </p>
                <p className={`mt-3 text-sm font-semibold leading-7 ${metric.tone === "dark" ? "text-[#E8EEF4]" : "text-[#334155]"}`}>
                  {metric.note}
                </p>
              </article>
            ))}
          </section>

          <section className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
            <SectionHeading title="Business Center Navigation" subtitle="Jump to the consulting workspaces that matter most right now." />
            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {centerNavigation.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-current={active ? "page" : undefined}
                    className={`group truevance-business-card business-nav-card focus-ring grid gap-3 rounded-2xl p-4 transition hover:-translate-y-0.5 ${
                      active ? "business-nav-card-active" : ""
                    }`}
                    title={`${item.label}: ${item.description}`}
                  >
                    <span className="flex items-start justify-between gap-3">
                      <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-[rgba(7,26,47,.9)] text-[#22E0D0] shadow-[0_18px_40px_rgba(7,26,47,.18)]">
                        <Icon className="size-5" aria-hidden="true" />
                      </span>
                      <ArrowRight className="size-5 shrink-0 text-[#FF6A2B] transition group-hover:translate-x-1" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block font-display text-2xl font-bold text-[#071A2F]">{item.label}</span>
                      <span className="mt-2 block text-sm font-semibold leading-7 text-[#334155]">{item.description}</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>

          <section className="truevance-business-empty rounded-2xl p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#17D4C4]">Empty state / setup note</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-[#071A2F]">{details.emptyStateTitle}</h2>
            <p className="mt-3 max-w-4xl text-sm font-semibold leading-7 text-[#334155]">{details.emptyStateDescription}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link to="/admin/truevance-business-center" className="admin-primary-button focus-ring">
                Back to Truevance Business Center
              </Link>
              <Link to="/admin/executive-dashboard" className="admin-secondary-button focus-ring">
                Open Executive Dashboard
              </Link>
            </div>
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.1fr_.9fr]">
            <article className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
              <SectionHeading title={details.lifecycleLabel} subtitle="The Business Center is built around the future consulting lifecycle." />
              <div className="mt-5 flex flex-wrap gap-2">
                {businessLifecycle.map((stage, index) => (
                  <span
                    key={stage}
                    className="inline-flex items-center gap-2 rounded-full border border-[rgba(7,26,47,.1)] bg-[rgba(34,224,208,.08)] px-3 py-2 text-xs font-black uppercase tracking-[0.14em] text-[#071A2F]"
                  >
                    <span className="grid size-5 place-items-center rounded-full bg-[rgba(7,26,47,.9)] text-[10px] text-[#F5F7FA]">{index + 1}</span>
                    {stage}
                  </span>
                ))}
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                {details.links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="truevance-business-card business-link-card focus-ring grid gap-2 rounded-2xl p-4 transition hover:-translate-y-0.5"
                    title={`${link.label}: ${link.description}`}
                  >
                    <span className="flex items-center justify-between gap-3">
                      <span className="font-black uppercase tracking-[0.16em] text-[#17D4C4]">{link.state || "Ready"}</span>
                      <ExternalLink className="size-4 shrink-0 text-[#FF6A2B]" aria-hidden="true" />
                    </span>
                    <span className="font-display text-2xl font-bold text-[#071A2F]">{link.label}</span>
                    <span className="text-sm font-semibold leading-7 text-[#334155]">{link.description}</span>
                  </Link>
                ))}
              </div>
            </article>

            <article className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
              <SectionHeading title="Operational layout" subtitle="The Business Center keeps the work visible even when a dedicated schema is still planned." dark />
              <div className="mt-5 grid gap-4">
                {details.sections.map((section) => (
                  <div key={section.title} className="rounded-2xl border border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.04)] p-4">
                    <h3 className="font-display text-2xl font-bold text-[#F5F7FA]">{section.title}</h3>
                    <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{section.description}</p>
                    <div className="mt-4 grid gap-3">
                      {section.items.map((item) => (
                        <div
                          key={item.title}
                          className={`rounded-xl border px-4 py-3 ${
                            item.state === "Live"
                              ? "border-[rgba(34,224,208,.22)] bg-[rgba(7,26,47,.68)]"
                              : item.state === "Ready"
                                ? "border-[rgba(214,178,74,.22)] bg-[rgba(214,178,74,.1)]"
                                : "border-[rgba(255,255,255,.12)] bg-[rgba(255,255,255,.04)]"
                          }`}
                          id={slugify(item.title)}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <h4 className="font-display text-xl font-bold text-[#F5F7FA]">{item.title}</h4>
                            <span className="rounded-full border border-[rgba(34,224,208,.22)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">
                              {item.state}
                            </span>
                          </div>
                          <p className="mt-2 text-sm font-semibold leading-7 text-[#E8EEF4]">{item.description}</p>
                          {item.to ? (
                            <Link to={item.to} className="focus-ring mt-3 inline-flex items-center gap-2 text-sm font-extrabold text-[#E0C96A]">
                              Open workspace
                              <ArrowRight className="size-4" aria-hidden="true" />
                            </Link>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
            <SectionHeading title="Future service workflow" subtitle="The Business Center is designed around the consulting lifecycle from inquiry through follow-up." />
            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              {businessLifecycle.map((stage, index) => (
                <article key={stage} className="rounded-2xl border border-[rgba(7,26,47,.08)] bg-[rgba(245,247,250,.98)] p-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#17D4C4]">Stage {String(index + 1).padStart(2, "0")}</p>
                  <h3 className="mt-2 font-display text-2xl font-bold text-[#071A2F]">{stage}</h3>
                  <p className="mt-2 text-sm font-semibold leading-7 text-[#334155]">Operational checkpoint in the consulting engagement lifecycle.</p>
                </article>
              ))}
            </div>
          </section>

          <section className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
            <SectionHeading title="Future-ready note" subtitle="These routes are intentionally structured so the next schema phase can add records without changing the shell." dark />
            <div className="mt-5 rounded-2xl border border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)] p-5">
              <p className="text-sm font-semibold leading-8 text-[#E8EEF4]">
                The Business Center is already route-scoped, branded, and accessible. The next phase can add schema-backed records without rebuilding the shell.
              </p>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}

function SectionHeading({ title, subtitle, dark = false }: { title: string; subtitle: string; dark?: boolean }) {
  return (
    <div className={`border-b pb-4 ${dark ? "border-[rgba(34,224,208,.16)]" : "border-[rgba(7,26,47,.08)]"}`}>
      <p className={`text-xs font-black uppercase tracking-[0.18em] ${dark ? "text-[#22E0D0]" : "text-[#17D4C4]"}`}>{TRUEVANCE_BRAND_NAME}</p>
      <h2 className={`mt-1 font-display text-3xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{title}</h2>
      <p className={`mt-3 max-w-4xl text-sm font-semibold leading-7 ${dark ? "text-[#E8EEF4]" : "text-[#334155]"}`}>{subtitle}</p>
    </div>
  );
}

function slugify(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
